import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  DnaPersonalityGuidelineCareerPathQueryResult,
  DnaPersonalityGuidelineRelationshipCompatibility
} from '@interfaces/schema';

/**
 * Report Persona Health Guideline Service.
 *
 * @exports
 * @class ReportPersonaHealthGuidelineService
 */
@Injectable()
export class ReportPersonaHealthGuidelineService {
  private readonly apollo = inject(Apollo);

  /**
   * Fetch Career Path.
   *
   * @returns {Observable<DnaPersonalityGuidelineCareerPathQueryResult>}
   * @memberof ReportPersonaHealthGuidelineService
   */
  careerPath(): Observable<DnaPersonalityGuidelineCareerPathQueryResult> {
    const query = gql`
      query PersonaCareerPath {
        DNA {
          PersonalityGuideline {
            CareerPath {
              findByUser {
                count
                results {
                  id
                  personalityGuidelineId
                  icon
                  jobField
                  profession
                  insight
                  position
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({ query })
      .pipe(
        map(
          ({ data }) =>
            (data as any)?.DNA?.PersonalityGuideline?.CareerPath
              ?.findByUser as DnaPersonalityGuidelineCareerPathQueryResult
        )
      );
  }

  /**
   * Fetch Relationship Compatibility.
   *
   * @returns {Observable<DnaPersonalityGuidelineRelationshipCompatibility>}
   * @memberof ReportPersonaHealthGuidelineService
   */
  relationshipCompatibility(): Observable<DnaPersonalityGuidelineRelationshipCompatibility> {
    const query = gql`
      query PersonaRelationshipCompatibility($include: [String]) {
        DNA {
          PersonalityGuideline {
            RelationshipCompatibility {
              findByUser(include: $include) {
                id
                topPersonality
                overview
                pGuidelineRelationshipInsight {
                  id
                  type
                  title
                  insight
                  position
                  pGuidelineRelationshipInsightDetail {
                    id
                    icon
                    title
                    insight
                    position
                  }
                }
                pGuidelineRelationshipScore {
                  id
                  score
                  personalityName
                  insight
                  position
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables: {
          include: [
            'rpx-personality-guideline-relationship-insight',
            'rpx-personality-guideline-relationship-score'
          ]
        }
      })
      .pipe(
        map(
          ({ data }) =>
            (data as any)?.DNA?.PersonalityGuideline?.RelationshipCompatibility
              ?.findByUser as DnaPersonalityGuidelineRelationshipCompatibility
        )
      );
  }
}
